import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "../../../assets/hospitalCloseIcon.svg";
import uploadIconGreen from "../../../assets/uploadIcon.svg";
import { CaseServices } from "../../../services/CaseServices";
import { ProcessingServices } from "../../../services/ProcessingServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

/**
 * Second-step upload modal for TPA Enhancement / Discharge
 * (mirrors admission UploadAdmissionInitiateRefDoc shell).
 * Processing API will be wired later.
 */
const TpaUploadReferenceDocModal = ({
  setOpen,
  selectedCase,
  AllCases,
  flowType, // "ENHANCEMENT" | "DISCHARGE"
  formData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [tpaUploading, setTpaUploading] = useState(false);
  const [tpaDocuments, setTpaDocuments] = useState([]);
  const [primaryChecks, setPrimaryChecks] = useState(null);
  const [enhancementList, setEnhancementList] = useState([]);
  // Original file names already uploaded in this session (for duplicate check)
  const [uploadedTpaFileNames, setUploadedTpaFileNames] = useState([]);
  // Ref keeps latest docs during async upload so append never loses prior uploads
  const tpaDocumentsRef = useRef([]);
  const fileInputRef = useRef(null);

  const isEnhancement = flowType === "ENHANCEMENT";
  const title = "Upload Document";
  const uploadCta = "Upload TPA Documents";
  const submitLabel = isEnhancement
    ? "Initiate Enhancement"
    : "Initiate Discharge";
  const targetStage = isEnhancement
    ? "Enhancement Documents Uploaded."
    : "Claim Documents uploaded";

  const normalizeDocName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  const mergeTpaDocuments = (previousDocs, incomingDocs) => {
    const prev = Array.isArray(previousDocs) ? previousDocs : [];
    const incoming = Array.isArray(incomingDocs) ? incomingDocs : [];
    const existingUrls = new Set(
      prev.map((doc) => String(doc?.url || "")).filter(Boolean),
    );
    const existingNames = new Set(
      prev.map((doc) => normalizeDocName(doc?.name)).filter(Boolean),
    );
    const toAdd = incoming.filter((doc) => {
      const url = String(doc?.url || "");
      const name = normalizeDocName(doc?.name);
      if (url && existingUrls.has(url)) return false;
      // Same classified name + same url already handled; allow same name only if url differs
      if (url) return true;
      if (name && existingNames.has(name)) return false;
      return true;
    });
    return [...prev, ...toAdd];
  };

  async function uploadTpaDocument(event) {
    const selectedFiles = Array.from(event.target.files || []);
    // Reset input so selecting the same files again still triggers onChange
    event.target.value = "";

    if (selectedFiles.length === 0) return;

    const allowedExtensions = [".pdf", ".png", ".jpg", ".jpeg"];
    const validFiles = selectedFiles.filter((file) =>
      allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)),
    );
    if (validFiles.length !== selectedFiles.length) {
      toast.error("Only PDF, PNG, JPG and JPEG files are allowed");
    }
    if (validFiles.length === 0) return;

    // Block re-upload of the same file already uploaded in this session
    const alreadyUploaded = new Set([
      ...uploadedTpaFileNames.map(normalizeDocName),
      ...(tpaDocuments || []).map((doc) => normalizeDocName(doc?.name)),
    ]);
    const seenInBatch = new Set();
    const duplicateFiles = [];
    const newFiles = [];

    validFiles.forEach((file) => {
      const key = normalizeDocName(file.name);
      if (alreadyUploaded.has(key) || seenInBatch.has(key)) {
        duplicateFiles.push(file);
      } else {
        seenInBatch.add(key);
        newFiles.push(file);
      }
    });

    if (duplicateFiles.length > 0) {
      toast.error("This document has already been uploaded.");
    }
    if (newFiles.length === 0) return;

    setTpaUploading(true);
    try {
      const uploadFormData = new FormData();
      newFiles.forEach((file) => uploadFormData.append("files", file));

      const response = isEnhancement
        ? await ProcessingServices.uploadEnhancementInitiateRefDoc(
            uploadFormData,
          )
        : await ProcessingServices.uploadDischargeInitiateRefDoc(
            uploadFormData,
          );
      isEnhancement
        ? setPrimaryChecks(response?.data?.progress_note_check)
        : setPrimaryChecks(response?.data?.discharge_check?.checks);
      // setPrimaryChecks(response?.data?.primary_check?.checks);
      // Store the classified documents ([{ name, key, url }])
      const uploadedDocuments = Array.isArray(response?.data)
        ? response.data
        : response?.data?.files || response?.data?.documents || [];
      const filteredDocuments = uploadedDocuments.filter(
        (document) => !document?.name?.includes("manifest"),
      );

      setUploadedTpaFileNames((prev) => [
        ...prev,
        ...newFiles.map((file) => normalizeDocName(file.name)),
      ]);
      // Always append to previously uploaded docs (never replace the list)
      const mergedDocuments = mergeTpaDocuments(
        tpaDocumentsRef.current,
        filteredDocuments,
      );
      tpaDocumentsRef.current = mergedDocuments;
      setTpaDocuments(mergedDocuments);
      toast.success(
        `${newFiles.length} document${newFiles.length > 1 ? "s" : ""} uploaded successfully`,
      );
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setTpaUploading(false);
    }
  }

  async function mapTpaDocuments(enhancementId) {
    const processingBaseUrl = process.env.REACT_APP_PROCESSING_API_URL;
    const documentType = isEnhancement
      ? enhancementList?.length > 0
        ? `ENHANCEMENT${enhancementList?.length + 1}`
        : "ENHANCEMENT1"
      : "ONDISCHARGEDOCUMENT";

    // Each classified document returned by the processing API becomes one document
    const classifiedDocuments = (tpaDocuments || []).map((document) => ({
      caseId: selectedCase?.id,
      documentType,
      documentName: document?.name,
      status: "INREVIEW",
      ...(isEnhancement && { enhancementId }),
      documenturlmasters: [
        {
          documentUrl: `${processingBaseUrl}${String(document?.url || "")}`,
          sequence: 1,
          status: "UPLOADED",
          deleted: false,
        },
      ],
    }));

    const payload = {
      documents: classifiedDocuments,
    };

    // Errors propagate to handleSubmit so the stage is not updated on failure
    const response = await CaseServices.mapSaveQueryDocuments(payload);
    console.log("TPA documents map response:", response?.data);
    toast.success("TPA documents mapped successfully");
  }

  const handleSubmit = async () => {
    if (!tpaDocuments.length) {
      toast.error("Please upload at least one reference document");
      return;
    }
    if (!selectedCase?.id) {
      toast.error("Case not found");
      return;
    }

    setSubmitting(true);
    let enhancementId = null;
    try {
      if (isEnhancement) {
        const enhancementResponse = await CaseServices.addEnhancementTpa([
          {
            detailType: "ENHANCEMENT",
            caseId: selectedCase.id,
            currentSubMasterStageId: selectedCase?.currentSubMasterStageId,
            details: {
              procedure: String(formData?.procedure || "").trim(),
              enhancementDate: new Date(),
              fromDate: formData?.fromDate,
              toDate: formData?.toDate,
              wardType: formData?.ward,
              enhancementDays: formData?.days,
            },
            status: 1,
            version: 0,
          },
        ]);
        enhancementId = enhancementResponse?.data?.enhancementId;
      } else {
        await CaseServices.addEnhancementTpa([
          {
            detailType: "DISCHARGE",
            caseId: selectedCase.id,
            currentSubMasterStageId: selectedCase?.currentSubMasterStageId,
            details: {
              dischargedDate: new Date(formData?.statustime),
              status: formData?.status,
              statustime: new Date(formData?.statustime),
            },
            status: 1,
            version: 0,
          },
        ]);
      }

      await mapTpaDocuments(enhancementId);

      await CaseServices.updateSubMasterStage(selectedCase.id, {
        commandCeneterStage: targetStage,
      });
      await AllCases?.();
      setOpen(false);
    } catch (error) {
      if (error?.response?.status === 420) {
        window.alert(
          "Documents are not yet mapped. Please try again after mapping.",
        );
      } else {
        handleApiErrorResponses(error);
      }
    } finally {
      setSubmitting(false);
    }
  };
  const getEnhancementList = async () => {
    try {
      const response = await CaseServices.getEnhancementsList(selectedCase.id);
      setEnhancementList(response.data.enhancements);
      console.log("enhancementList:", enhancementList);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    getEnhancementList();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="fixed top-0 left-0 h-screen w-screen flex justify-center items-center bg-[#017C7180] z-[1200]">
      <div className="relative h-[80%] w-[90%] 2xl:w-[80%] bg-white rounded-[30px] p-10">
        <div
          className="absolute top-3 right-4 cursor-pointer"
          onClick={() => setOpen(false)}
        >
          <img src={CloseIcon} alt="close-icon" height={53} width={52} />
        </div>

        <div className="flex justify-between items-end mr-8">
          <div className="text-[24px] 2xl:text-[30px]">
            {tpaDocuments?.length > 0
              ? "Classified Reference Documents"
              : title}
          </div>
        </div>

        <div
          style={{ border: "1px solid #E4E4E4" }}
          className="w-[100%] h-[80%] flex justify-center items-start gap-2 flex-wrap overflow-y-scroll mt-4 p-1 box-border rounded-[20px]"
        >
          {tpaDocuments?.length > 0 ? (
            <div className="relative w-full min-h-full">
              {tpaUploading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-[20px] bg-[#FFFFFFCC]">
                  <CircularProgress />
                  <div className="text-sm font-medium text-[#017C71]">
                    Processing document… it will appear shortly
                  </div>
                </div>
              )}
              <div className="w-full flex flex-wrap gap-4">
                {tpaDocuments.map((document, idx) => {
                  const fileUrl = document?.url
                    ? `${process.env.REACT_APP_PROCESSING_API_URL}${document.url
                        .split("/")
                        .map(encodeURIComponent)
                        .join("/")}`
                    : "";
                  const fileName = document?.name || "";
                  const isPdf = /\.pdf$/i.test(fileName);
                  const isImage = /\.(jpe?g|png)$/i.test(fileName);
                  // "OPD_OPD_Notes.pdf" -> "OPD OPD Notes"
                  const label = fileName
                    .replace(/\.[^.]+$/, "")
                    .replace(/_+/g, " ")
                    .trim();

                  return (
                    <a
                      key={`${fileName}-${document?.url || idx}`}
                      href={fileUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={fileName}
                      style={{ border: "1px solid #E4E4E4" }}
                      className="relative block w-[256px] h-[290px] box-border bg-white rounded-[17px] overflow-hidden no-underline cursor-pointer"
                    >
                      <div className="absolute top-3 left-4 z-[2] text-[24px] leading-[100%] text-[#000000]">
                        {idx + 1}.
                      </div>

                      {/* Document preview */}
                      <div className="absolute inset-0 bg-white">
                        {fileUrl && isPdf ? (
                          <iframe
                            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            title={fileName}
                            className="w-full h-full border-0 pointer-events-none"
                          />
                        ) : fileUrl && isImage ? (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[14px] text-[#969696]">
                            Preview not available
                          </div>
                        )}
                      </div>

                      {/* Document name pill */}
                      <div className="absolute bottom-3 left-3 right-3 z-[2] h-[40px] px-4 flex items-center justify-center rounded-full bg-[#80808099] backdrop-blur-[4px]">
                        <span className="text-[16px] font-medium text-[#FFFFFF] truncate">
                          {label}
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="h-full w-full flex justify-center items-center">
              {tpaUploading ? (
                <CircularProgress />
              ) : (
                <div
                  style={{ border: "1px solid #017C71" }}
                  className="flex items-center gap-3 py-3 px-5 text-[#017C71] rounded-full cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadCta} <img src={uploadIconGreen} alt="upload-icon" />
                </div>
              )}
            </div>
          )}
        </div>

        <input
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
          className="hidden"
          ref={fileInputRef}
          onChange={uploadTpaDocument}
        />

        {tpaDocuments.length > 0 && (
          <div className="mt-1">
            <div className="flex justify-end items-center gap-3 text-xs 2xl:text-sm text-white -tracking-[3%] uppercase">
              {tpaUploading ? (
                <div className="flex items-center gap-2 normal-case">
                  <CircularProgress size={22} sx={{ color: "#017C71" }} />
                  <span className="text-xs 2xl:text-sm text-[#017C71]">
                    Processing document…
                  </span>
                </div>
              ) : (
                <div
                  className="py-2 2xl:py-3 px-4 2xl:px-5 text-[#017C71] cursor-pointer underline underline-offset-3"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload New Document
                </div>
              )}
            </div>
            <div className="w-full flex items-center justify-center mt-2">
              {submitting ? (
                <CircularProgress />
              ) : (
                <div
                  className="text-sm -tracking-[0.03em] py-2 2xl:py-3 px-7 2xl:px-8 bg-[#017C71] text-white rounded-full cursor-pointer"
                  onClick={() => {
                    if (isEnhancement && primaryChecks?.passed === false) {
                      toast.error(primaryChecks?.reason);
                      return;
                    }
                    if (!isEnhancement && primaryChecks[0]?.passed === false) {
                      toast.error(primaryChecks[0]?.reason);
                      return;
                    }
                    handleSubmit();
                  }}
                >
                  {submitLabel}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TpaUploadReferenceDocModal;
